<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import * as Yup from 'yup'
import { useField, useForm } from 'vee-validate'
import LogoSpinner from '@/components/app/LogoSpinner.vue'
import i18n from '@/plugins/i18n'
import { useAppStore } from '@/stores/app'
import { useStudentStore } from '@/stores/student'
import { useYearsAndSubgroupsQuery } from '@/composable/queries/Dara/organisation'
import { useUpdateGroupsMutation } from '@/composable/queries/Dara/student'

const appStore = useAppStore()
const studentStore = useStudentStore()
const { t } = useI18n()

const loading = computed(() => isYearsAndSubgroupsPending.value)
const saving = computed(() => updateGroupsMutation.isPending.value)

const schema = computed(() => {
  const baseSchema = {
    year_group_id: Yup.number().required(t('validation.required')),
  }

  if (studentStore.organisation.require_subgroup_selection) {
    return Yup.object({
      ...baseSchema,
      year_sub_group_id: Yup.number().required(t('validation.required')),
    })
  }

  return Yup.object(baseSchema)
})

const form = useForm({ validationSchema: schema })

const yearGroupId = useField<number>('year_group_id')
const yearSubGroupId = useField<number>('year_sub_group_id')

const updateGroupsMutation = useUpdateGroupsMutation(computed(() => studentStore.id))

const organisationId = computed(() => studentStore.organisation?.id || 0)

const { data: yearsAndSubgroupsResponse, isPending: isYearsAndSubgroupsPending } = useYearsAndSubgroupsQuery(organisationId, computed(() => !!organisationId.value))
const groups = computed(() => (yearsAndSubgroupsResponse.value as unknown as App.Http.Resources.MyFJV2.GroupResource[]) || [])
const selectedYear = computed(() => groups.value?.find((year) => year.id === yearGroupId.value.value))

const handleSave = () => {
  form.validate().then(({ valid }) => {
    if (!valid) return

    const data = {
      year_group_id: yearGroupId.value.value,
      ...(studentStore.organisation.require_subgroup_selection && { year_sub_group_id: yearSubGroupId.value.value })
    }

    updateGroupsMutation.mutate(data, {
      onSuccess: () => {
        appStore.studentSelectYearModal = false
        appStore.setSnackbar({
          colour: 'success',
          text: t('components.student.select_year.selections_saved'),
        })
      }
    })
  })
}

// Reset year subgroup when year group changes
watch(() => yearGroupId.value.value, () => {
  form.resetField('year_sub_group_id')
})

// Auto-open modal when year/group selection is required
watch(() => studentStore.yearOrYearGroupSelectionRequired, (required) => {
  if (required && !studentStore.is_trial) {
    appStore.studentSelectYearModal = true
  }
}, { immediate: true })
</script>

<template>
  <VDialog
    v-model="appStore.studentSelectYearModal"
    width="auto"
    max-width="35rem"
    persistent
  >
    <VCard>
      <VCardTitle>
        {{ $t('components.student.select_year.select_year') }}
      </VCardTitle>
      <VCardText>
        <div v-if="$i18n.locale == 'en'" class="mb-3">
            Your School has requested that you set your <span class="text-secondary">Year</span> <span v-if="studentStore.organisation.require_subgroup_selection">and <span class="text-secondary">{{ studentStore.organisation.year_sub_group_known_as }} </span></span> before continuing with <span class="text-primary">MyFutureJourney</span>.
        </div>
        <div v-else class="mb-3">
            {{ $t('components.student.select_year.modal_text', { appName: 'MyFutureJourney' }) }}
        </div>

        <div class="d-flex justify-center">
          <LogoSpinner v-if="loading" />
        </div>

        <VForm v-if="!loading" @submit.prevent="handleSave" class="w-100">
          <VLabel>{{ $t('components.student.select_year.year') }}</VLabel>
          <VSelect
            v-model="yearGroupId.value.value"
            :error-messages="yearGroupId.errorMessage.value"
            :placeholder="$t('actions.please_select')"
            :items="groups"
            :disabled="isYearsAndSubgroupsPending"
            item-value="id"
            item-title="name"
            variant="outlined"
            density="compact"
            class="w-100"
          />
          <template v-if="studentStore.organisation.require_subgroup_selection">
            <VLabel>{{ studentStore.organisation.year_sub_group_known_as }}</VLabel>
            <VSelect
              v-model="yearSubGroupId.value.value"
              :error-messages="yearSubGroupId.errorMessage.value"
              :placeholder="$t('actions.please_select')"
              :items="selectedYear?.year_groups || []"
              :disabled="isYearsAndSubgroupsPending || !yearGroupId.value.value"
              item-value="id"
              item-title="name"
              variant="outlined"
              density="compact"
              class="w-100"
            />
          </template>

        </VForm>
      </VCardText>
      <VCardActions class="d-flex justify-space-between ma-2">
        <VBtn
          v-if="!studentStore.yearOrYearGroupSelectionRequired"
          color="grey"
          variant="outlined"
          class="text-none"
          @click="appStore.studentSelectYearModal = false"
        >
          {{ i18n.global.t('actions.cancel') }}
        </VBtn>
        <VBtn
          color="secondary"
          variant="flat"
          class="text-none"
          :disabled="saving || loading"
          @click="handleSave"
        >
          {{ i18n.global.t('actions.save') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
